<template>
  <div>
    <!-- TOOLBAR -->
    <div class="uk-grid"
      v-show="displayed"
      data-uk-grid-margin
      v-inject:toolbar>
      <div class="uk-width-large-2-3 uk-text-left">
        <div class="uk-grid" data-uk-grid-margin>
          <div class="uk-width-medium-1-3 uk-width-small-1-1 tm-filter-bulk-width">
            <!-- FILTER BY -->
            <div class="uk-button-group">
              <tm-button disabled class="tm-button-medium-half">
                <i class="uk-icon-filter uk-hidden-large"></i>
                <span class="uk-visible-large">Filter by</span>
              </tm-button>
              <!-- installed -->
              <tm-button half
                :style="filters.installed ? 'primary' : ''"
                title="Installed"
                icon="bolt"
                @click="filters.installed = !filters.installed">
              </tm-button>
              <!-- outdated -->
              <tm-button half
                :style="filters.outdated ? 'primary' : ''"
                title="Outdated"
                icon="level-down"
                @click="filters.outdated = !filters.outdated">
              </tm-button>
              <!-- subscribed -->
              <tm-button half
                v-if="authenticated"
                :style="filters.subscribed ? 'primary' : ''"
                title="Subscribed"
                icon="ticket"
                @click="filters.subscribed = !filters.subscribed">
              </tm-button>
            </div>
          </div>
          <div class=" uk-width-medium-1-3 uk-width-small-1-1 tm-filter-bulk-width">
            <!-- BULK ACTIONS -->
            <div class="uk-button-group">
              <tm-button disabled class="tm-button-medium-half">
                <i class="uk-icon-check-circle-o uk-hidden-large"></i>
                <span class="uk-visible-large">Bulk</span>
              </tm-button>
              <!-- download -->
              <tm-button half
                style="primary"
                :disabled="selected.length !== 0 && !validateSelected('download')"
                title="Download all or selected"
                icon="download"
                @click="selected.length
                  ? download(selected)
                  : download(getSelection({ subscribed: true, outdated: true }))">
              </tm-button>
              <!-- install -->
              <tm-button half
                style="primary"
                :disabled="selected.length !== 0 && !validateSelected('install')"
                :icon="'bolt'"
                title="Install/Update all or selected"
                @click="selected.length
                  ? install(selected)
                  : install(getSelection({ subscribed: true }))">
              </tm-button>
              <!-- toggle -->
              <tm-button half
                style="primary"
                :disabled="!validateSelected('toggle')"
                title="Toggle selected"
                icon="toggle-on"
                @click="toggle(selected)">
              </tm-button>
              <!-- uninstall -->
              <tm-button half
                style="danger"
                :disabled="!validateSelected('uninstall')"
                title="Uninstall selected"
                icon="trash-o"
                @click="uninstall(selected)">
              </tm-button>
              <tm-button
                class="uk-hidden-large tm-button-medium-half"
                v-show="selected.length"
                title="Clear selection"
                icon="times"
                @click="selection = {}">
              </tm-button>
            </div>
            <tm-button half
              class="uk-visible-large"
              v-show="selected.length"
              title="Clear selection"
              icon="times"
              @click="selection = {}">
            </tm-button>
          </div>
        </div>
      </div>
      <div class="uk-width-large-1-3 uk-width-medium-1-3 uk-width-small-1-1 tm-large-right">
        <!-- auth -->
        <tm-button
          :disabled="server.offline"
          title="Authenticate yourself"
          :text="authenticated ? 'Authenticated' : 'Authenticate'"
          @click="showAuth">
        </tm-button>
        <!-- reload -->
        <tm-button
          :disabled="server.offline"
          title="Reload extensions list"
          text="Reload"
          @click="reload">
        </tm-button>
      </div>
    </div>
    <!-- EXTENSIONS LIST -->
    <template v-for="(group, extensions) in grouped">
      <div class="uk-margin-large-top uk-text-bold"
        v-text="group">
      </div>
      <extension v-for="ext in extensions" track-by="name"
        :type="ext.type"
        :name="ext.name"
        :title="ext.title"
        :price="ext.price"
        :version="ext.version"
        :url="ext.url"
        :installed="ext.installed"
        :installed-version="ext.installedVersion"
        :outdated="ext.outdated"
        :core="ext.core"
        :deprecated="ext.deprecated"
        :enabled="ext.enabled"
        :subscribed="ext.subscribed"
        :download-url="ext.downloadUrl"
        :selected.sync="selection[ext.name]"
        :offline="server.offline">
      </extension>
    </template>
  </div>
</template>

<script>
import {
  filter as _filter,
  every as _every,
  some as _some,
  find as _find,
  pick as _pick,
  pickBy as _pickBy,
  groupBy as _groupBy,
  mapKeys as _mapKeys
} from 'lodash'
import Extension from './Extension'
import { show as showAuth } from '../../vuex/actions/authentication'
import { extensions as extensionsList } from '../../vuex/getters'
import {
  retrieve,
  download,
  install,
  uninstall as doUninstall,
  toggle
} from '../../vuex/actions/extensions'

export default {
  name: 'extensions',
  activate: function (done) {
    this.retrieve().then(() => done())
  },
  components: { Extension },
  watch: {
    'authenticated': function () {
      this.reload()
    }
  },
  data: () => ({
    selection: {},
    filters: {
      installed: false,
      outdated: false,
      subscribed: false,
      enabled: false
    }
  }),
  vuex: {
    getters: {
      extensionsList,
      authenticated: state => state.products.authentication.status,
      view: state => state.products.view,
      server: state => state.products.server
    },
    actions: {
      showAuth,
      retrieve,
      download,
      install,
      doUninstall,
      toggle,
      promptConfirm: ({ dispatch }, text) => {
        dispatch('SHOW_MODAL_CONFIRM', { text,
          icon: ['uk-icon-exclamation', 'uk-text-warning']
        })
      },
      reload: function ({ dispatch }) {
        dispatch('SHOW_MODAL', { spinner: true, block: true })
        this.retrieve(true).then(() => dispatch('HIDE_MODAL'))
      }
    }
  },
  computed: {
    filtered: function () {
      return Object.keys(_pickBy(this.filters)).length
    },
    displayed: function () {
      return this.view === this.$options.name
    },
    extensions: function () {
      return this.filters
        ? _filter(this.extensionsList, _pickBy(this.filters))
        : this.extensionsList
    },
    grouped: function () {
      return _mapKeys(_pick(
        _groupBy(this.extensions, 'type'),
        ['Core', 'Element', 'Plugin', 'Module', 'App']
      ), (value, key) => key === 'Core'
        ? 'Core'
        : key + 's'
      )
    },
    // on view and selectable extensions
    selectable: function () {
      return _filter(this.extensions,
        ext => ext.installed || ext.subscribed
      )
    },
    selected: function () {
      const selected = Object.keys(_pickBy(this.selection))
      return _filter(this.selectable, (ext) =>
        (selected.indexOf(ext.name) !== -1)
      )
      // keep the array of name values
      .map(ext => ext.name)
    }
  },
  methods: {
    getSelection: function (filter) {
      return _filter(this.extensions, filter).map(ext => ext.name)
    },
    uninstall: function (extensions) {
      this.$once('modal:confirmed', () => this.doUninstall(extensions))
      let msg = extensions.length > 1
        ? `You are about to uninstall ${extensions.length} extensions`
        : `You are about to uninstall
            ${_find(this.extensions, ['name', extensions[0]]).title}`
      this.promptConfirm(msg)
    },
    // a function to validate if all selected
    // extensions supports a specific action
    validateSelected: function (action) {
      if (!this.selected.length || this.server.offline) {
        return false
      }
      // get selected extensions data
      const extensions = _filter(this.extensions, (ext) =>
        this.selected.indexOf(ext.name) !== -1
      )
      switch (action) {
        case 'uninstall':
          return !_some(extensions, ['type', 'Core']) &&
            _every(extensions, { installed: true })
        case 'toggle':
          return _every(extensions, { type: 'Plugin', installed: true })
        case 'download':
        case 'install':
          return _every(extensions, 'subscribed') &&
            _filter(extensions, { type: 'Element' }).length < 2
      }
    }
  }
}
</script>

<style lang="less">
@import "../../../node_modules/uikit/src/less/core/variables.less";

#toolbar .uk-grid + .uk-grid, #toolbar .uk-grid-margin, #toolbar .uk-grid > * > .uk-panel + .uk-panel {
  margin-top: 10px;
}

@media (min-width: @breakpoint-large) {
  .tm-large-right {
    text-align: right !important;
  }
  .tm-filter-bulk-width {
    width: auto !important;
  }
}

@media (max-width: @breakpoint-medium-max) {
  .tm-button-medium-half {
    min-width: 45px !important;
  }
}
</style>



/** WEBPACK FOOTER **
 ** Extensions.vue?d421b326
 **/